import type { MentionActorSource } from '../../state/mentionUtils';
import { resolveDialogueSpeakerChip } from '../dialogueLines';
import { resolveTelopSpeakerView, type TelopLine } from '../telopLines';
import { DialogueSpeakerChip } from './DialogueSpeakerChip';
import { groupConsecutiveLines, telopSpeakerGroupKey } from './speakerLineGroups';
import styles from './DialogueLines.module.css';

const EMPTY_ACTORS: MentionActorSource[] = [];

/**
 * 構造化モード (`telop_lines` が非 NULL) のテロップ読み取り表示
 * (docs/features/scenario-telop-speaker-plan.md §3.7)。話者チップ + 文言。
 * 俳優は DialogueLinesView と同じ顔写真+バッジ、ナレーションはマイク、キャスト外の
 * 呼称 (登場人物宣言を含む) はダミー人物+頭文字 (ホバーで名前)、話者なしはチップを出さない。
 * 同一話者が連続する行は 1 グループにまとめ、チップを 1 枚だけ出す (speakerLineGroups.ts)。
 * セリフ欄 (吹き出し) と違い、テロップは塗り・枠なしのプレーン表示のまま。
 */
export function TelopLinesView({
  lines,
  actors = EMPTY_ACTORS,
}: {
  lines: TelopLine[];
  actors?: MentionActorSource[];
}) {
  if (lines.length === 0) {
    return <span className={styles.emptyHint}>（テロップなし）</span>;
  }
  const groups = groupConsecutiveLines(lines, (line) => telopSpeakerGroupKey(line.speaker));
  return (
    <div className={`${styles.view} ${styles.telopView}`} data-testid="telop-lines-view">
      {groups.map((group) => {
        const view = resolveTelopSpeakerView(group.lines[0]!.speaker);
        let chip: React.ReactNode = null;
        if (view.kind === 'actor') {
          const resolved = resolveDialogueSpeakerChip(
            { label: view.label, actor_id: view.actorId, actor_kind: view.actorKind }, actors,
          );
          chip = resolved
            ? <DialogueSpeakerChip badge={resolved.badge} thumbnailUrl={resolved.thumbnailUrl} name={resolved.name} size="lg" />
            : <DialogueSpeakerChip badge="" thumbnailUrl={null} name={view.label} size="lg" />;
        } else if (view.kind === 'narration') {
          chip = <DialogueSpeakerChip badge="" thumbnailUrl={null} name="ナレーション" size="lg" glyph="mic" />;
        } else if (view.kind === 'label') {
          chip = <DialogueSpeakerChip badge="" thumbnailUrl={null} name={view.label} size="lg" />;
        }
        return (
          <div className={styles.viewRow} key={group.firstIndex} data-speaker-kind={view.kind} data-speaker-key={group.key}>
            {chip}
            <div className={styles.telopTexts}>
              {group.lines.map((line, idx) => (
                <span className={styles.lineText} key={idx}>{line.text}</span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
